/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

import { useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { AppState } from "store/store.type"
import { actionCreator } from "store/store"
import { useEnablePartialMenu } from "hooks/useEnablePartialMenu"
import { isMenuFilterActive } from "utils/menu_helper"
import { useMenuOverviewDepartments } from "./useMenuOverviewDepartments"
import { useMenuOverviewSubDepartments } from "./useMenuOverviewSubDepartments"

/**
 * Syncs overview department/sub-department selection from displayv2 when filters
 * are active under partial menu.
 *
 * V3 hierarchy selection leaves overview.subDepartments empty; displayv2 must
 * re-populate Redux via selectDepartment even when the department ID is unchanged.
 *
 * Only runs when enablePartialMenu=true AND filters are active (legacy UI).
 * When no filters are active, V3 components handle their own auto-selection.
 */
export const useMenuAutoSelection = () => {
  const dispatch = useDispatch()
  const enablePartialMenu = useEnablePartialMenu()

  const {
    overview: {
      department: selectedDepartment,
      subDepartment: selectedSubDepartment,
      sellingTime,
    },
    isMartDepartmentStructure,
    itemFilter,
  } = useSelector((state: AppState) => state.menu)

  // Check if filters are active - only run auto-selection when using legacy components
  const isFilterActive = isMenuFilterActive(itemFilter, sellingTime)

  // Get filtered data from hooks
  const {
    filteredDepartments,
    isLoading: isDepartmentsLoading,
  } = useMenuOverviewDepartments()
  const {
    filteredSubDepartments,
    isLoading: isSubDepartmentsLoading,
  } = useMenuOverviewSubDepartments()

  const selectedDepartmentID = selectedDepartment?.itemClassID

  useEffect(() => {
    if (
      enablePartialMenu &&
      isFilterActive &&
      isMartDepartmentStructure &&
      !isDepartmentsLoading &&
      filteredDepartments.length > 0
    ) {
      const matchedDepartment = selectedDepartment
        ? filteredDepartments.find(
            dept => dept.itemClassID === selectedDepartment.itemClassID,
          )
        : null

      dispatch(
        actionCreator.menu.selectDepartment(
          matchedDepartment ?? filteredDepartments[0],
        ),
      )
    }
    // selectedDepartment is read for ID matching only; omit from deps to avoid
    // re-sync loops — filteredDepartments updates on filter/data changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enablePartialMenu,
    isFilterActive,
    isMartDepartmentStructure,
    isDepartmentsLoading,
    filteredDepartments,
    dispatch,
  ])

  useEffect(() => {
    if (
      enablePartialMenu &&
      isFilterActive &&
      isMartDepartmentStructure &&
      selectedDepartmentID &&
      !isSubDepartmentsLoading &&
      filteredSubDepartments.length > 0
    ) {
      const matchedSubDepartment = selectedSubDepartment
        ? filteredSubDepartments.find(
            sd => sd.itemClassID === selectedSubDepartment.itemClassID,
          )
        : null

      dispatch(
        actionCreator.menu.selectSubDepartment(
          matchedSubDepartment ?? filteredSubDepartments[0],
        ),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enablePartialMenu,
    isFilterActive,
    isMartDepartmentStructure,
    isSubDepartmentsLoading,
    filteredSubDepartments,
    selectedDepartmentID,
    dispatch,
  ])
}
